import { findTransitionFromStatus } from 'features/libra/lifecycle/lifecycleSideEffects'
import type { LifecycleConfig } from 'features/libra/lifecycle/lifecycleTypes'
import { LifecycleTriggerType } from 'features/libra/lifecycle/lifecycleTypes'
import { applyFinalEligibility } from 'features/libra/workflow/finalEligibility'
import { getInactiveStepIds } from 'features/libra/workflow/inactiveSteps'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'

import type { LibraTicket, UpdateTicketArgs } from './libraTicketsApi'

type TicketPatch = Omit<UpdateTicketArgs, 'ticketId'>

/**
 * The patch that moves a ticket to `stepId`, including the lifecycle
 * transition that reaching that step triggers, if one is configured.
 */
export function buildStepMovePatch(
  lifecycleConfig: LifecycleConfig | undefined,
  ticketStatus: string | undefined,
  stepId: string
): TicketPatch {
  const patch: TicketPatch = { stepId }
  if (!lifecycleConfig) return patch

  const transition = findTransitionFromStatus(lifecycleConfig, ticketStatus, {
    type: LifecycleTriggerType.STEP_REACHED,
    stepId,
  })
  if (transition) patch.ticketStatus = transition.to
  return patch
}

/**
 * Where the ticket pointer must move so it never rests on a step the field
 * values deactivate. Sits on a skipped step: jump to the first active step
 * after it. A step that just became active again behind the pointer: return
 * to the earliest one.
 */
function resolveStepRelocation(
  workflow: LibraWorkflow,
  latest: LibraTicket,
  patch: TicketPatch
): string | undefined {
  const before = getInactiveStepIds(workflow, latest.fieldValues)
  const after = getInactiveStepIds(
    workflow,
    patch.fieldValues ?? latest.fieldValues
  )
  if (before.size === 0 && after.size === 0) return undefined

  const currentId = patch.stepId ?? latest.stepId
  const currentIndex = workflow.stepIndices.get(currentId)
  if (currentIndex === undefined) return undefined

  if (after.has(currentId)) {
    return workflow.steps
      .slice(currentIndex + 1)
      .find((step) => !after.has(step.id))?.id
  }

  return workflow.steps
    .slice(0, currentIndex)
    .find((step) => before.has(step.id) && !after.has(step.id))?.id
}

/**
 * The stage a backward move to `targetId` undoes: the `from` of a STEP_REACHED
 * transition into `status` whose step lies after the target.
 */
function resolveBackwardStatus(
  workflow: LibraWorkflow,
  status: string | undefined,
  targetId: string,
  currentId: string
): string | undefined {
  const targetIndex = workflow.stepIndices.get(targetId)
  const currentIndex = workflow.stepIndices.get(currentId)
  if (targetIndex === undefined || currentIndex === undefined) return undefined
  if (targetIndex >= currentIndex || status === undefined) return undefined

  return workflow.lifecycleConfig?.transitions.find((transition) => {
    if (transition.trigger.type !== LifecycleTriggerType.STEP_REACHED) {
      return false
    }
    const reachedIndex = workflow.stepIndices.get(transition.trigger.stepId)
    return (
      transition.to === status &&
      reachedIndex !== undefined &&
      reachedIndex > targetIndex
    )
  })?.from
}

/**
 * Completes a write with the values the workflow derives from it. A patch's
 * `fieldValues` is a whole-ticket replacement (every writer merges through
 * `buildFieldValuesPatch`), so a derived field sees every source regardless of
 * which step or scope wrote. `latest` is the committed ticket the write
 * applies to, used to move the step pointer off steps the write deactivates.
 * Returns the same patch when nothing needs deriving.
 */
export function deriveTicketPatch(
  workflow: LibraWorkflow,
  latest: LibraTicket,
  patch: TicketPatch | null
): TicketPatch | null {
  if (!patch) return patch

  const config = workflow.finalEligibilityConfig
  const derivedValues =
    patch.fieldValues && config
      ? applyFinalEligibility(config, patch.fieldValues)
      : patch.fieldValues
  const derived =
    derivedValues === patch.fieldValues
      ? patch
      : { ...patch, fieldValues: derivedValues }

  const relocateTo = resolveStepRelocation(workflow, latest, derived)
  if (!relocateTo) return derived

  const currentStatus = derived.ticketStatus ?? latest.ticketStatus
  const move = buildStepMovePatch(
    workflow.lifecycleConfig,
    currentStatus,
    relocateTo
  )
  const movedStatus =
    move.ticketStatus ??
    resolveBackwardStatus(
      workflow,
      currentStatus,
      relocateTo,
      derived.stepId ?? latest.stepId
    )
  return {
    ...derived,
    stepId: move.stepId,
    ...(derived.ticketStatus === undefined && movedStatus !== undefined
      ? { ticketStatus: movedStatus }
      : {}),
  }
}
